import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useApi } from "../api/ApiContext";
import {
  Box, Typography, Button, Alert, LinearProgress, IconButton,
  Tooltip, Chip, TextField, Snackbar, CircularProgress, Slider,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import FlipCameraAndroidIcon from "@mui/icons-material/FlipCameraAndroid";
import { motion, AnimatePresence } from "framer-motion";

// ─── Image compression ────────────────────────────────────────────────────────
const MAX_PX = 1920;
const QUALITY = 0.82;
const SKIP_BELOW_KB = 300;

async function compressImage(file) {
  if (!file.type.startsWith("image/")) return file;
  if (file.size < SKIP_BELOW_KB * 1024) return file;
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { naturalWidth: w, naturalHeight: h } = img;
      if (w > MAX_PX || h > MAX_PX) {
        const ratio = Math.min(MAX_PX / w, MAX_PX / h);
        w = Math.round(w * ratio); h = Math.round(h * ratio);
      }
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      c.toBlob((blob) => {
        if (!blob || blob.size >= file.size) { resolve(file); return; }
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
      }, "image/jpeg", QUALITY);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

async function generateVideoThumbnail(file) {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;

    const capture = () => {
      try {
        const vw = video.videoWidth, vh = video.videoHeight;
        if (!vw || !vh) { URL.revokeObjectURL(url); resolve(null); return; }
        const s = Math.min(vw, vh);
        const c = document.createElement("canvas");
        c.width = c.height = 160;
        c.getContext("2d").drawImage(video, (vw - s) / 2, (vh - s) / 2, s, s, 0, 0, 160, 160);
        URL.revokeObjectURL(url);
        resolve(c.toDataURL("image/jpeg", 0.8));
      } catch { URL.revokeObjectURL(url); resolve(null); }
    };

    video.onloadeddata = capture;
    video.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    video.src = url;
  });
}
// ─── Thumbnail generation (400×400 square JPEG, sent alongside original) ────
const THUMB_SIZE = 400;
const THUMB_QUALITY = 0.78;

function generateThumbnail(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);

    const finish = (canvas) => {
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (!blob) { resolve(null); return; }
        resolve(new File([blob], `thumb_${Date.now()}.jpg`, { type: "image/jpeg" }));
      }, "image/jpeg", THUMB_QUALITY);
    };

    if (file.type.startsWith("video/")) {
      const vid = document.createElement("video");
      vid.preload = "auto"; vid.muted = true; vid.playsInline = true;
      vid.onloadeddata = () => {
        try {
          const vw = vid.videoWidth, vh = vid.videoHeight;
          if (!vw || !vh) { URL.revokeObjectURL(url); resolve(null); return; }
          const s = Math.min(vw, vh);
          const c = document.createElement("canvas");
          c.width = c.height = THUMB_SIZE;
          c.getContext("2d").drawImage(vid, (vw - s) / 2, (vh - s) / 2, s, s, 0, 0, THUMB_SIZE, THUMB_SIZE);
          finish(c);
        } catch { URL.revokeObjectURL(url); resolve(null); }
      };
      vid.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      vid.src = url;
    } else {
      const img = new Image();
      img.onload = () => {
        const { naturalWidth: w, naturalHeight: h } = img;
        const s = Math.min(w, h);
        const c = document.createElement("canvas");
        c.width = c.height = THUMB_SIZE;
        c.getContext("2d").drawImage(img, (w - s) / 2, (h - s) / 2, s, s, 0, 0, THUMB_SIZE, THUMB_SIZE);
        finish(c);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    }
  });
}
// ─────────────────────────────────────────────────────────────────────────────

const FUN_FILTERS = [
  { label: "Brak",        value: "none",                                                           type: "css" },
  { label: "🌸 Różowa",   value: "hue-rotate(310deg) saturate(2) brightness(1.2)",                 type: "css" },
  { label: "🎉 Impreza",  value: "invert(1) contrast(1.8)",                                        type: "css" },
  { label: "📼 VHS",      value: "sepia(1) contrast(0.8) blur(1px) grayscale(0.5)",                type: "css" },
  { label: "🐸 Żaba",     value: "hue-rotate(90deg) saturate(3)",                                  type: "css" },
  { label: "🌨️ Zima",    value: "contrast(1.2) grayscale(1) brightness(1.1)",                     type: "css" },
  { label: "🌅 Zachód",   value: "sepia(0.7) saturate(2) hue-rotate(340deg) brightness(1.05)",    type: "css" },
  { label: "🌊 Ocean",    value: "hue-rotate(180deg) saturate(2.2) brightness(0.9)",               type: "css" },
  { label: "🌈 Vivid",    value: "saturate(3) contrast(1.2)",                                      type: "css" },
  { label: "🎨 Pop Art",  value: "saturate(4) contrast(1.5) hue-rotate(30deg)",                    type: "css" },
  { label: "🌸 Pastel",   value: "saturate(0.6) brightness(1.3) contrast(0.85)",                   type: "css" },
  { label: "🔮 Neon",     value: "invert(0.1) saturate(3.5) hue-rotate(270deg) brightness(1.3)",  type: "css" },
  { label: "🌙 Noc",      value: "brightness(0.55) saturate(0.4) contrast(1.3)",                   type: "css" },
  { label: "💛 Złoto",    value: "sepia(0.6) saturate(2.2) hue-rotate(15deg) brightness(1.1)",    type: "css" },
  { label: "🐟 Rybie oko", value: "fisheye",   type: "fun" },
  { label: "🔍 Szkiełko", value: "magnify",    type: "fun" },
  { label: "🪞 Lustro",   value: "mirror",     type: "fun" },
  { label: "🌀 Wir",      value: "whirl",      type: "fun" },
  { label: "🎲 Piksel",   value: "pixelate",   type: "fun" },
];

// ─── Canvas pixel filters ─────────────────────────────────────────────────────
function applyMirror(imageData) {
  const { width, height, data } = imageData;
  const out = new ImageData(width, height); const dst = out.data;
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const i = (y * width + x) * 4;
    const s = (y * width + (x < width / 2 ? x : width - x - 1)) * 4;
    dst[i] = data[s]; dst[i+1] = data[s+1]; dst[i+2] = data[s+2]; dst[i+3] = data[s+3];
  }
  return out;
}

function applyFisheye(imageData) {
  const { width: W, height: H, data } = imageData;
  const out = new ImageData(W, H); const dst = out.data;
  const cx = W / 2, cy = H / 2, maxR = Math.min(cx, cy);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const dx = (x - cx) / maxR, dy = (y - cy) / maxR;
    const r = Math.sqrt(dx*dx + dy*dy), rn = r === 0 ? 0 : Math.pow(r, 0.8);
    const sx = Math.round(cx + dx*rn*maxR), sy = Math.round(cy + dy*rn*maxR);
    const i = (y*W + x)*4;
    if (sx >= 0 && sx < W && sy >= 0 && sy < H) {
      const s = (sy*W + sx)*4;
      dst[i] = data[s]; dst[i+1] = data[s+1]; dst[i+2] = data[s+2]; dst[i+3] = data[s+3];
    } else { dst[i+3] = 255; }
  }
  return out;
}

function applyMagnify(imageData) {
  const { width: W, height: H, data } = imageData;
  const out = new ImageData(W, H); const dst = out.data;
  const cx = W / 2, cy = H / 2, radius = Math.min(cx, cy) / 2;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const dx = x - cx, dy = y - cy, dist = Math.sqrt(dx*dx + dy*dy);
    const sx = dist < radius ? Math.round(cx + dx/2) : x;
    const sy = dist < radius ? Math.round(cy + dy/2) : y;
    const i = (y*W + x)*4;
    if (sx >= 0 && sx < W && sy >= 0 && sy < H) {
      const s = (sy*W + sx)*4;
      dst[i] = data[s]; dst[i+1] = data[s+1]; dst[i+2] = data[s+2]; dst[i+3] = data[s+3];
    }
  }
  return out;
}

function applyWhirl(imageData, strength = 3) {
  const { width: W, height: H, data } = imageData;
  const out = new ImageData(W, H); const dst = out.data;
  const cx = W / 2, cy = H / 2, maxR = Math.min(cx, cy);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const dx = x - cx, dy = y - cy, r = Math.sqrt(dx*dx + dy*dy);
    const twist = r < maxR ? strength * (1 - r / maxR) : 0;
    const angle = Math.atan2(dy, dx) + twist;
    const sx = Math.round(cx + r * Math.cos(angle)), sy = Math.round(cy + r * Math.sin(angle));
    const i = (y*W + x)*4;
    if (sx >= 0 && sx < W && sy >= 0 && sy < H) {
      const s = (sy*W + sx)*4;
      dst[i] = data[s]; dst[i+1] = data[s+1]; dst[i+2] = data[s+2]; dst[i+3] = data[s+3];
    } else { dst[i+3] = 255; }
  }
  return out;
}

function applyPixelate(imageData, blockSize = 14) {
  const { width: W, height: H, data } = imageData;
  const out = new ImageData(W, H); const dst = out.data;
  for (let y = 0; y < H; y += blockSize) for (let x = 0; x < W; x += blockSize) {
    const s = (y*W + x)*4;
    const r = data[s], g = data[s+1], b = data[s+2], a = data[s+3];
    for (let dy = 0; dy < blockSize && y+dy < H; dy++)
      for (let dx = 0; dx < blockSize && x+dx < W; dx++) {
        const i = ((y+dy)*W + (x+dx))*4;
        dst[i] = r; dst[i+1] = g; dst[i+2] = b; dst[i+3] = a;
      }
  }
  return out;
}

function applyFunFilter(name, imageData) {
  if (name === "fisheye")  return applyFisheye(imageData);
  if (name === "magnify")  return applyMagnify(imageData);
  if (name === "mirror")   return applyMirror(imageData);
  if (name === "whirl")    return applyWhirl(imageData);
  if (name === "pixelate") return applyPixelate(imageData);
  return imageData;
}
// ─────────────────────────────────────────────────────────────────────────────

const getFileKey = (file) => `${file.name}_${file.size}_${file.lastModified}`;

export default function Upload() {
  const { user } = useAuth();
  const api = useApi();
  const location = useLocation();

  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [thumbnailMap, setThumbnailMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [photosSent, setPhotosSent] = useState(0);
  const [imageLimit, setImageLimit] = useState(8);
  const [copied, setCopied] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const [filter, setFilter] = useState("none");
  const [caption, setCaption] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(10);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [previewFilter, setPreviewFilter] = useState("none");
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

  const fileInputRef = useRef();
  const videoRef = useRef();
  const canvasRef = useRef();
  const liveEffectRef = useRef();
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  const showToast = (msg, severity = "success") => setToast({ open: true, msg, severity });

  const params = new URLSearchParams(location.search);
  const userIdParam = params.get("user_id");
  let effectiveUserId = "";
  if (userIdParam && userIdParam !== "null" && userIdParam !== "undefined") {
    effectiveUserId = userIdParam;
  } else if (user?.id) {
    effectiveUserId = String(user.id);
  }
  const localStorageKey = `uploaded_count_user_${effectiveUserId || "unknown"}`;

  useEffect(() => {
    if (!effectiveUserId) return;
    api.get(`/get_user_limit.php?user_id=${effectiveUserId}`)
      .then(res => setImageLimit(Number(res.data.image_limit || 8)));
  }, [effectiveUserId, api]);

  useEffect(() => {
    if (!effectiveUserId) return;
    const sent = parseInt(localStorage.getItem(localStorageKey) || "0", 10);
    setPhotosSent(isNaN(sent) ? 0 : sent);
  }, [effectiveUserId, localStorageKey]);

  useEffect(() => {
    if (!files.length) { setPreviews([]); return; }
    const urls = files.filter(Boolean).map(f => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach(url => URL.revokeObjectURL(url));
  }, [files]);

  // Generate thumbnails for video files
  useEffect(() => {
    files.forEach(file => {
      if (!file?.type?.startsWith("video/")) return;
      const key = getFileKey(file);
      if (thumbnailMap[key]) return;
      generateVideoThumbnail(file).then(thumb => {
        if (thumb) setThumbnailMap(prev => ({ ...prev, [key]: thumb }));
      });
    });
  }, [files]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── File handling ───────────────────────────────────────────────────────────
  const handleFileChange = async (newFiles) => {
    const arr = Array.from(newFiles).filter(f => f.type.startsWith("image/"));
    if (!arr.length) return;
    const compressed = await Promise.all(arr.map(compressImage));
    setFiles(prev => [...prev, ...compressed]);
  };

  const removePreview = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // ─── Drag and drop ───────────────────────────────────────────────────────────
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileChange(e.dataTransfer.files);
  };

  // ─── Camera ──────────────────────────────────────────────────────────────────
  const getStream = async (facing) => {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: true,
      });
    } catch {
      return navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: true });
    }
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      showToast("Twoja przeglądarka nie obsługuje kamery.", "error");
      return;
    }
    setShowCamera(true);
    try {
      const stream = await getStream(facingMode);
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
    } catch {
      showToast("Brak dostępu do kamery. Sprawdź ustawienia przeglądarki.", "error");
      setShowCamera(false);
    }
  };

  const stopCameraStream = () => {
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
    if (mediaRecorderRef.current?.state !== "inactive") mediaRecorderRef.current?.stop();
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
  };

  const stopCamera = () => {
    stopCameraStream();
    setShowCamera(false);
    setIsRecording(false);
    setRecordingSeconds(10);
    setZoom(1);
    discardPreview();
  };

  const previewUrlRef = useRef(null);
  // Keep ref in sync so stopCamera can always revoke the latest URL
  useEffect(() => { previewUrlRef.current = previewUrl; }, [previewUrl]);

  const discardPreview = () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    setPreviewFile(null);
    setPreviewUrl(null);
  };

  const retakeClip = () => {
    discardPreview();
    startCamera(); // reopens the full-screen camera overlay
  };

  const useClip = () => {
    if (!previewFile) return;
    setFiles(prev => [...prev, previewFile]);
    setPreviewFile(null);
    setPreviewUrl(null);
    setShowCamera(false);
    showToast("Klip dodany! 🎬");
  };

  const flipCamera = async () => {
    const next = facingMode === "environment" ? "user" : "environment";
    setFacingMode(next);
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    }
    try {
      const stream = await getStream(next);
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
    } catch {
      showToast("Nie można przełączyć kamery.", "error");
    }
  };

  // ─── Video recording ─────────────────────────────────────────────────────────
  const startRecording = () => {
    if (!videoRef.current?.srcObject) return;
    recordedChunksRef.current = [];

    // Always capture from canvas — filters (CSS via ctx.filter + pixel) are baked in
    const stream = canvasRef.current.captureStream(30);
    (videoRef.current.srcObject?.getAudioTracks() || []).forEach(t => stream.addTrack(t));

    const mimeType = ["video/webm;codecs=vp9", "video/webm", "video/mp4"]
      .find(t => { try { return MediaRecorder.isTypeSupported(t); } catch { return false; } }) || "video/webm";

    let mr;
    try { mr = new MediaRecorder(stream, { mimeType }); }
    catch { mr = new MediaRecorder(stream); }
    mediaRecorderRef.current = mr;

    mr.ondataavailable = e => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: mimeType });
      const ext = mimeType.includes("mp4") ? "mp4" : "webm";
      const file = new File([blob], `clip_${Date.now()}.${ext}`, { type: mimeType });
      const url = URL.createObjectURL(file);
      stopCameraStream();
      setIsRecording(false);
      setRecordingSeconds(10);
      setShowCamera(false); // close camera overlay — preview takes over
      setPreviewFile(file);
      setPreviewUrl(url);
    };

    mr.start(100);
    setIsRecording(true);
    setRecordingSeconds(10);
    let secs = 10;
    recordingTimerRef.current = setInterval(() => {
      secs--;
      setRecordingSeconds(secs);
      if (secs <= 0) stopRecording();
    }, 1000);
  };

  const stopRecording = () => {
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
    if (mediaRecorderRef.current?.state !== "inactive") mediaRecorderRef.current?.stop();
  };

  // ─── Live canvas loop (always active when camera is open) ───────────────────
  useEffect(() => {
    if (!showCamera) return;
    let stopped = false;
    const sel = () => FUN_FILTERS.find(f => f.value === filter);

    const loop = () => {
      if (!videoRef.current || !canvasRef.current) { liveEffectRef.current = requestAnimationFrame(loop); return; }
      const video = videoRef.current, canvas = canvasRef.current;
      if (video.videoWidth === 0) { liveEffectRef.current = requestAnimationFrame(loop); return; }

      const vw = video.videoWidth, vh = video.videoHeight;
      // Size canvas to match its rendered container (fixes zoom: canvas was rendered at
      // native video resolution e.g. 1920px which appeared massively zoomed on mobile)
      const container = canvas.parentElement;
      const cw = container.clientWidth || vw;
      const ch = container.clientHeight || vh;
      canvas.width = cw; canvas.height = ch;
      const ctx = canvas.getContext("2d");

      // Cover crop: fit video to canvas aspect ratio, then apply zoom
      const videoAspect = vw / vh;
      const displayAspect = cw / ch;
      let srcW, srcH;
      if (videoAspect > displayAspect) { srcH = vh / zoom; srcW = srcH * displayAspect; }
      else { srcW = vw / zoom; srcH = srcW / displayAspect; }
      const sx = (vw - srcW) / 2, sy = (vh - srcH) / 2;
      const f = sel();

      // CSS filters applied via canvas element CSS — no ctx.filter here to avoid double-baking
      // Mirror for front camera
      if (facingMode === "user") { ctx.save(); ctx.translate(cw, 0); ctx.scale(-1, 1); }
      ctx.drawImage(video, sx, sy, srcW, srcH, 0, 0, cw, ch);
      if (facingMode === "user") ctx.restore();

      // Apply fun (pixel) filters
      if (f?.type === "fun") {
        const imageData = ctx.getImageData(0, 0, cw, ch);
        if (filter === "fisheye") {
          const fc = document.createElement("canvas"); fc.width = cw; fc.height = ch;
          fc.getContext("2d").putImageData(applyFisheye(imageData), 0, 0);
          const d = Math.min(cw, ch);
          ctx.clearRect(0, 0, cw, ch);
          ctx.save(); ctx.beginPath(); ctx.arc(cw/2, ch/2, d/2, 0, 2*Math.PI); ctx.clip();
          ctx.drawImage(fc, (cw-d)/2, (ch-d)/2, d, d, 0, 0, cw, ch); ctx.restore();
        } else {
          const processed = applyFunFilter(filter, imageData);
          if (processed !== imageData) ctx.putImageData(processed, 0, 0);
        }
      }

      if (!stopped) liveEffectRef.current = requestAnimationFrame(loop);
    };

    liveEffectRef.current = requestAnimationFrame(loop);
    return () => {
      stopped = true;
      if (liveEffectRef.current) cancelAnimationFrame(liveEffectRef.current);
    };
  }, [showCamera, filter, facingMode, zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (val) => setFilter(val);

  const takePhoto = () => {
    if (!canvasRef.current) return;
    const live = canvasRef.current;
    const f = FUN_FILTERS.find(f => f.value === filter);
    // For CSS filters: bake onto a snapshot canvas (CSS filter on element isn't captured by toBlob)
    // For pixel filters: they're already in the live canvas pixels — just copy
    if (f?.type === "css" && filter !== "none") {
      const snap = document.createElement("canvas");
      snap.width = live.width; snap.height = live.height;
      const ctx = snap.getContext("2d");
      ctx.filter = filter;
      ctx.drawImage(live, 0, 0);
      ctx.filter = "none";
      snap.toBlob(blob => {
        if (!blob) return;
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
        const url = URL.createObjectURL(file);
        stopCameraStream(); setShowCamera(false);
        setPreviewFile(file); setPreviewUrl(url); setPreviewFilter("none");
      }, "image/jpeg", QUALITY);
    } else {
      live.toBlob(blob => {
        if (!blob) return;
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
        const url = URL.createObjectURL(file);
        stopCameraStream(); setShowCamera(false);
        setPreviewFile(file); setPreviewUrl(url); setPreviewFilter("none");
      }, "image/jpeg", QUALITY);
    }
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!effectiveUserId) { showToast("Brak użytkownika docelowego.", "error"); return; }
    if (files.length === 0) { showToast("Nie wybrano żadnych plików.", "error"); return; }
    if (photosSent + files.length > imageLimit) {
      showToast(`Możesz wysłać maksymalnie ${imageLimit} plików z tego urządzenia.`, "warning");
      return;
    }
    setLoading(true);
    // Generate thumbnails in parallel before uploading
    const thumbnails = await Promise.all(files.map(generateThumbnail));
    const formData = new FormData();
    files.forEach(file => formData.append("files[]", file));
    // thumbnails[] index must match files[] index so backend pairs them correctly
    thumbnails.forEach(thumb => formData.append("thumbnails[]", thumb ?? new Blob([], { type: "image/jpeg" })));
    formData.append("user_id", effectiveUserId);
    formData.append("caption", caption);
    try {
      const response = await fetch("https://ksiega.kalmarry.pl/new/upload.php", { method: "POST", body: formData });
      const res = await response.json();
      if (res.success) {
        const now = photosSent + files.length;
        localStorage.setItem(localStorageKey, String(now));
        setPhotosSent(now);
        setFiles([]);
        setCaption("");
        showToast(`Wysłano ${files.length} ${files.length === 1 ? "plik" : "pliki"}! 🎉`);
      } else {
        showToast(res.message || "Błąd podczas wysyłania.", "error");
      }
    } catch {
      showToast("Błąd podczas wysyłania. Sprawdź połączenie.", "error");
    }
    setLoading(false);
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/upload?user_id=${effectiveUserId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showToast("Link skopiowany! 📋");
    } catch {}
  };

  const remainingSlots = imageLimit - photosSent;
  const isLimitReached = photosSent >= imageLimit;

  if (!effectiveUserId) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#fff", display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#111" }}>Nie można określić galerii</Typography>
          <Typography sx={{ color: "#aaa", mt: 0.5, fontSize: 14 }}>Użyj poprawnego linku lub zaloguj się.</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      {/* ── Post-recording preview ────────────────────────────────────────────── */}
      <AnimatePresence>
        {previewUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 1500, background: "#000",
              display: "flex", flexDirection: "column",
            }}
          >
            {/* Media preview (image or video) */}
            {(() => {
              const isImg = previewFile?.type?.startsWith("image/");
              return (
                <Box sx={{ flex: 1, position: "relative", overflow: "hidden" }}>
                  {isImg ? (
                    <img
                      src={previewUrl}
                      alt=""
                      style={{
                        position: "absolute", inset: 0, width: "100%", height: "100%",
                        objectFit: "contain",
                        // CSS filter fallback for old iOS where ctx.filter may not have baked it
                        filter: previewFilter !== "none" ? previewFilter : "none",
                      }}
                    />
                  ) : (
                    <video
                      key={previewUrl}
                      src={previewUrl}
                      autoPlay loop playsInline muted controls
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  )}

                  {/* Top bar */}
                  <Box sx={{
                    position: "absolute", top: 0, left: 0, right: 0,
                    pt: "max(env(safe-area-inset-top), 12px)",
                    pb: 2, px: 2,
                    background: "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <IconButton onClick={() => { discardPreview(); setShowCamera(false); }}
                      sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(6px)" }}>
                      <CloseIcon />
                    </IconButton>
                    <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 600, letterSpacing: 0.5 }}>
                      {isImg ? "Podgląd zdjęcia" : "Podgląd klipu"}
                    </Typography>
                    <Box sx={{ width: 40 }} />
                  </Box>
                </Box>
              );
            })()}

            {/* Bottom action bar */}
            {(() => {
              const isImg = previewFile?.type?.startsWith("image/");
              return (
                <Box sx={{
                  background: "rgba(0,0,0,0.85)",
                  backdropFilter: "blur(12px)",
                  pb: "max(env(safe-area-inset-bottom), 24px)",
                  pt: 2, px: 3,
                  display: "flex", gap: 2,
                }}>
                  <Button
                    variant="outlined" fullWidth onClick={retakeClip}
                    sx={{
                      borderColor: "rgba(255,255,255,0.4)", color: "#fff", fontWeight: 700,
                      borderRadius: 3, py: 1.4, fontSize: 15,
                      "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                    }}
                  >
                    {isImg ? "Zrób ponownie" : "Nagraj ponownie"}
                  </Button>
                  <Button
                    variant="contained" fullWidth onClick={useClip}
                    sx={{
                      background: "linear-gradient(90deg, #EB578A 0%, #C0145E 100%)",
                      fontWeight: 700, borderRadius: 3, py: 1.4, fontSize: 15,
                      boxShadow: "0 4px 14px rgba(235,87,138,0.4)",
                      "&:hover": { background: "linear-gradient(90deg, #d94d7e 0%, #a81050 100%)" },
                    }}
                  >
                    {isImg ? "Użyj zdjęcia ✓" : "Użyj klipu ✓"}
                  </Button>
                </Box>
              );
            })()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Full-screen camera overlay ─────────────────────────────────────────── */}
      <AnimatePresence>
        {showCamera && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 1400, background: "#000",
              display: "flex", flexDirection: "column",
            }}
          >
            {/* Video feed */}
            <Box sx={{ flex: 1, position: "relative", overflow: "hidden" }}>
              {/* Video is source for canvas — always hidden visually */}
              <video
                ref={videoRef}
                style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
                playsInline muted autoPlay
              />
              {/* Canvas renders live view — sized to fill container, CSS filter for live preview */}
              <canvas
                ref={canvasRef}
                style={{
                  position: "absolute", inset: 0,
                  width: "100%", height: "100%",
                  filter: FUN_FILTERS.find(f => f.value === filter)?.type === "css" && filter !== "none"
                    ? filter : undefined,
                }}
              />

              {/* Top controls */}
              <Box sx={{
                position: "absolute", top: 0, left: 0, right: 0,
                pt: "max(env(safe-area-inset-top), 10px)",
                pb: 2, px: 2,
                background: "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 100%)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <IconButton onClick={stopCamera}
                  sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.15)", backdropFilter: "blur(6px)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
                  <CloseIcon />
                </IconButton>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontWeight: 600 }}>
                    {facingMode === "user" ? "Przednia" : "Tylna"}
                  </Typography>
                  <IconButton onClick={flipCamera}
                    sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.2)", backdropFilter: "blur(6px)", "&:hover": { bgcolor: "rgba(255,255,255,0.35)" }, border: "1.5px solid rgba(255,255,255,0.4)" }}>
                    <FlipCameraAndroidIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Zoom slider — right side */}
              <Box sx={{
                position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5,
                height: 160,
              }}>
                <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 10, fontWeight: 700 }}>
                  {zoom.toFixed(1)}×
                </Typography>
                <Slider
                  orientation="vertical"
                  min={1} max={4} step={0.1}
                  value={zoom}
                  onChange={(_, v) => setZoom(v)}
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    "& .MuiSlider-track": { bgcolor: "#EB578A" },
                    "& .MuiSlider-thumb": { bgcolor: "#fff", width: 18, height: 18, border: "2px solid #EB578A" },
                    "& .MuiSlider-rail": { bgcolor: "rgba(255,255,255,0.3)" },
                  }}
                />
                <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 9 }}>ZOOM</Typography>
              </Box>

              {/* Recording badge */}
              {isRecording && (
                <Box sx={{
                  position: "absolute", top: "max(env(safe-area-inset-top), 10px)", left: 0, right: 0,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  px: 2, pt: 6, pointerEvents: "none",
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, bgcolor: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", borderRadius: 3, px: 1.5, py: 0.6 }}>
                    <Box sx={{
                      width: 8, height: 8, borderRadius: "50%", bgcolor: "#ff4444",
                      "@keyframes blink": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.2 } },
                      animation: "blink 1s infinite",
                    }} />
                    <Typography sx={{ color: "#fff", fontSize: 11, fontWeight: 800, letterSpacing: 1.5 }}>REC</Typography>
                  </Box>
                  <Box sx={{ bgcolor: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", borderRadius: 3, px: 1.5, py: 0.6 }}>
                    <Typography sx={{ color: "#ff4444", fontSize: 20, fontWeight: 900, lineHeight: 1 }}>
                      {recordingSeconds}s
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Bottom controls */}
            <Box sx={{
              background: "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 70%, transparent 100%)",
              pb: "max(env(safe-area-inset-bottom), 20px)",
              pt: 1,
            }}>
              {/* Filter chips */}
              {!isRecording && (
                <Box sx={{
                  display: "flex", gap: 0.8, overflowX: "auto",
                  px: 2, pt: 1.5, pb: 1.5,
                  scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" },
                }}>
                  {FUN_FILTERS.map(f => (
                    <Chip
                      key={f.value}
                      label={f.label}
                      size="small"
                      onClick={() => handleFilterChange(f.value)}
                      sx={{
                        flexShrink: 0, cursor: "pointer", fontSize: 12,
                        bgcolor: filter === f.value ? "rgba(235,87,138,0.9)" : "rgba(255,255,255,0.12)",
                        color: "#fff",
                        fontWeight: filter === f.value ? 700 : 400,
                        border: filter === f.value ? "2px solid rgba(235,87,138,1)" : "1px solid rgba(255,255,255,0.2)",
                        backdropFilter: "blur(8px)",
                        "&:hover": { bgcolor: filter === f.value ? "rgba(235,87,138,1)" : "rgba(255,255,255,0.22)" },
                        "& .MuiChip-label": { color: "#fff" },
                      }}
                    />
                  ))}
                </Box>
              )}

              {/* Shutter row */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: "10%", pb: 0.5, pt: 0.5 }}>
                {/* Last capture preview */}
                <Box sx={{ width: 52, height: 52 }}>
                  {files.length > 0 && (() => {
                    const last = files[files.length - 1];
                    const src = last?.type?.startsWith("video/")
                      ? thumbnailMap[getFileKey(last)]
                      : previews[previews.length - 1];
                    return src ? (
                      <Box sx={{ width: 52, height: 52, borderRadius: 2, overflow: "hidden", border: "2.5px solid rgba(255,255,255,0.7)", boxShadow: "0 2px 10px rgba(0,0,0,0.5)" }}>
                        <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </Box>
                    ) : null;
                  })()}
                </Box>

                {/* Main shutter / stop button */}
                <Box
                  onClick={isRecording ? stopRecording : takePhoto}
                  sx={{
                    width: 78, height: 78, borderRadius: "50%",
                    border: isRecording ? "4px solid #ff4444" : "4px solid rgba(255,255,255,0.95)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer",
                    transition: "transform 0.12s",
                    "&:active": { transform: "scale(0.9)" },
                  }}
                >
                  {isRecording ? (
                    <Box sx={{ width: 28, height: 28, borderRadius: "6px", bgcolor: "#ff4444" }} />
                  ) : (
                    <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.96)" }} />
                  )}
                </Box>

                {/* Record button */}
                <Box sx={{ width: 52, height: 52, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {!isRecording && (
                    <Box
                      onClick={startRecording}
                      sx={{
                        width: 52, height: 52, borderRadius: "50%",
                        border: "3px solid rgba(255,80,80,0.75)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer",
                        bgcolor: "rgba(255,68,68,0.15)",
                        backdropFilter: "blur(6px)",
                        transition: "transform 0.12s",
                        "&:active": { transform: "scale(0.88)" },
                      }}
                    >
                      <RadioButtonCheckedIcon sx={{ color: "#ff4444", fontSize: 28 }} />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Main upload form (Instagram "New Post" style) ───────────────────── */}
      <Box sx={{ minHeight: "100vh", bgcolor: "#fff" }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header row */}
          <Box sx={{
            position: "sticky", top: 0, zIndex: 10,
            bgcolor: "#fff", borderBottom: "1px solid #efefef",
            px: 2, py: 1.2,
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <Typography sx={{ fontWeight: 700, fontSize: 17, color: "#111" }}>
              Nowe zdjęcie
            </Typography>
            <Button
              onClick={handleSubmit}
              disabled={loading || isLimitReached || files.length === 0}
              sx={{
                fontWeight: 700, fontSize: 14, textTransform: "none",
                color: files.length > 0 && !isLimitReached ? "#0095f6" : "#aaa",
                minWidth: 0, p: 0,
                "&:hover": { bgcolor: "transparent", color: "#00376b" },
                "&.Mui-disabled": { color: "#aaa" },
              }}
            >
              {loading ? <CircularProgress size={18} sx={{ color: "#0095f6" }} /> : "Udostępnij"}
            </Button>
          </Box>

          {/* Media picker / preview area */}
          {!isLimitReached && previews.length === 0 && (
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                aspectRatio: "1/1",
                maxHeight: 400,
                width: "100%",
                bgcolor: isDragging ? "#f0f0f0" : "#fafafa",
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                cursor: "pointer",
                borderBottom: "1px solid #efefef",
                transition: "background 0.15s",
              }}
            >
              <ImageIcon sx={{ fontSize: 54, color: "#c7c7c7", mb: 1.5 }} />
              <Typography sx={{ fontWeight: 600, fontSize: 20, color: "#c7c7c7" }}>
                {isDragging ? "Upuść tutaj" : "Dodaj zdjęcia"}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#ccc", mt: 0.5 }}>
                lub przeciągnij i upuść
              </Typography>
            </Box>
          )}

          {/* Selected media grid */}
          {previews.length > 0 && (
            <Box>
              {/* Main preview — first image large */}
              <Box sx={{ position: "relative", aspectRatio: "1/1", bgcolor: "#000", overflow: "hidden" }}>
                {(() => {
                  const file = files[0];
                  const isVideo = file?.type?.startsWith("video/");
                  const src = isVideo ? (thumbnailMap[getFileKey(file)] || null) : previews[0];
                  return src ? (
                    <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  ) : isVideo ? (
                    <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <CircularProgress sx={{ color: "#EB578A" }} />
                    </Box>
                  ) : null;
                })()}
                {/* Count badge if multiple */}
                {previews.length > 1 && (
                  <Box sx={{ position: "absolute", top: 10, right: 10, bgcolor: "rgba(0,0,0,0.6)", borderRadius: 2, px: 1, py: 0.3 }}>
                    <Typography sx={{ color: "#fff", fontSize: 11, fontWeight: 700 }}>1 / {previews.length}</Typography>
                  </Box>
                )}
              </Box>

              {/* Thumbnail strip */}
              <Box sx={{ display: "flex", gap: "2px", borderBottom: "1px solid #efefef", overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
                {previews.map((src, i) => {
                  const file = files[i];
                  const isVideo = file?.type?.startsWith("video/");
                  const thumbSrc = isVideo ? (thumbnailMap[getFileKey(file)] || null) : src;
                  return (
                    <Box key={i} sx={{ position: "relative", flexShrink: 0, width: 80, height: 80, bgcolor: "#111" }}>
                      {thumbSrc ? (
                        <img src={thumbSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      ) : (
                        <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <CircularProgress size={16} sx={{ color: "#EB578A" }} />
                        </Box>
                      )}
                      {isVideo && (
                        <Box sx={{ position: "absolute", bottom: 2, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
                          <Typography sx={{ fontSize: 8, bgcolor: "rgba(0,0,0,0.65)", color: "#fff", px: 0.5, borderRadius: 0.5, fontWeight: 700 }}>VIDEO</Typography>
                        </Box>
                      )}
                      <IconButton size="small" onClick={() => removePreview(i)}
                        sx={{ position: "absolute", top: 2, right: 2, p: 0.3, bgcolor: "rgba(0,0,0,0.55)", "&:hover": { bgcolor: "rgba(0,0,0,0.75)" } }}>
                        <CloseIcon sx={{ fontSize: 10, color: "#fff" }} />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Camera / Gallery action buttons */}
          {!isLimitReached && (
            <Box sx={{ display: "flex", gap: 0, borderBottom: "1px solid #efefef" }}>
              <Box onClick={startCamera}
                sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 2.5, gap: 0.6, cursor: "pointer", "&:active": { bgcolor: "#fafafa" } }}>
                <CameraAltIcon sx={{ fontSize: 26, color: "#262626" }} />
                <Typography sx={{ fontSize: 11, color: "#262626", fontWeight: 500 }}>Aparat</Typography>
              </Box>
              <Box sx={{ width: "1px", bgcolor: "#efefef" }} />
              <Box onClick={() => fileInputRef.current?.click()}
                sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 2.5, gap: 0.6, cursor: "pointer", "&:active": { bgcolor: "#fafafa" } }}>
                <ImageIcon sx={{ fontSize: 26, color: "#262626" }} />
                <Typography sx={{ fontSize: 11, color: "#262626", fontWeight: 500 }}>Biblioteka</Typography>
              </Box>
              <input ref={fileInputRef} type="file" multiple accept="image/*"
                onChange={e => handleFileChange(e.target.files)} style={{ display: "none" }} />
            </Box>
          )}

          {/* Caption input */}
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <TextField
              fullWidth
              placeholder="Dodaj podpis…"
              variant="standard"
              value={caption}
              onChange={e => setCaption(e.target.value)}
              multiline
              minRows={2}
              inputProps={{ maxLength: 200 }}
              sx={{
                "& .MuiInput-root": { fontSize: 15, color: "#262626" },
                "& .MuiInput-root:before": { borderBottom: "none" },
                "& .MuiInput-root:after": { borderBottom: "none" },
                "& .MuiInput-root:hover:not(.Mui-disabled):before": { borderBottom: "none" },
                "& textarea::placeholder": { color: "#aaa" },
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
              <Typography sx={{ fontSize: 11, color: "#ccc" }}>{caption.length}/200</Typography>
            </Box>
          </Box>

          <Box sx={{ height: "1px", bgcolor: "#efefef", mx: 2 }} />

          {/* Progress & link section */}
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            {/* Limit bar */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Typography sx={{ fontSize: 13, color: "#8e8e8e" }}>Wysłane</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: isLimitReached ? "#EB578A" : "#262626" }}>
                {photosSent} / {imageLimit}
              </Typography>
            </Box>
            <Box sx={{ height: 2, bgcolor: "#efefef", borderRadius: 1, overflow: "hidden", mb: 1.5 }}>
              <Box sx={{
                height: "100%", borderRadius: 1,
                width: `${Math.min(100, (photosSent / imageLimit) * 100)}%`,
                background: "linear-gradient(90deg, #EB578A, #C0145E)",
                transition: "width 0.5s",
              }} />
            </Box>

            {/* Share link */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "#fafafa", borderRadius: 2, px: 1.5, py: 1, border: "1px solid #efefef" }}>
              <Typography sx={{ fontSize: 12, color: "#aaa", flexShrink: 0 }}>Link:</Typography>
              <Typography sx={{ fontSize: 12, color: "#0095f6", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexGrow: 1, minWidth: 0 }}>
                {`${window.location.origin}/upload?user_id=${effectiveUserId}`}
              </Typography>
              <IconButton size="small" onClick={handleCopyLink} sx={{ p: 0.5, flexShrink: 0 }}>
                <ContentCopyIcon sx={{ fontSize: 15, color: copied ? "#4caf50" : "#8e8e8e" }} />
              </IconButton>
            </Box>
          </Box>

          {/* Submit button (bottom, for when header is scrolled past) */}
          {files.length > 0 && !isLimitReached && (
            <Box sx={{ px: 2, pt: 1, pb: 3 }}>
              <Button
                fullWidth onClick={handleSubmit} disabled={loading}
                sx={{
                  py: 1.2, background: "linear-gradient(90deg, #EB578A 0%, #C0145E 100%)",
                  color: "#fff", fontWeight: 700, fontSize: 15, borderRadius: "12px",
                  textTransform: "none",
                  boxShadow: "0 4px 14px rgba(235,87,138,0.3)",
                  "&:hover": { background: "linear-gradient(90deg, #d94d7e 0%, #a81050 100%)" },
                  "&.Mui-disabled": { background: "#f0d0dc", boxShadow: "none", color: "#fff" },
                }}
              >
                {loading
                  ? <><CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />Wysyłanie…</>
                  : `Udostępnij (${files.length})`}
              </Button>
              {loading && (
                <LinearProgress sx={{ mt: 1, borderRadius: 1, height: 3, bgcolor: "#ffd6e7", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg, #EB578A, #C0145E)" } }} />
              )}
            </Box>
          )}

          {isLimitReached && (
            <Box sx={{ px: 2, pt: 1, pb: 3 }}>
              <Box sx={{ bgcolor: "#fff8e1", borderRadius: 2, px: 2, py: 1.5, border: "1px solid #ffe082" }}>
                <Typography sx={{ fontSize: 13, color: "#f57c00", fontWeight: 600, textAlign: "center" }}>
                  Osiągnięto limit zdjęć
                </Typography>
              </Box>
            </Box>
          )}
        </motion.div>
      </Box>

      <Snackbar open={toast.open} autoHideDuration={2800} onClose={() => setToast(t => ({ ...t, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} sx={{ borderRadius: 3, fontWeight: 600, boxShadow: "0 4px 14px rgba(0,0,0,0.12)" }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
